import { create } from 'zustand';
import { employeesApi } from '../../../../services/api';

const initialState = {
  employees: [],
  searchTerm: '',
  orderBy: 'fullName',
  orderDirection: 'asc',
  limit: 20,
  hasNextPage: false,
  endCursor: null,
  totalCount: 0,
  loading: false,
  error: null,
};

const useEmployeeStore = create((set, get) => {
  /**
   * Helper to build query params from state and overrides
   */
  const buildParams = (overrides = {}) => {
    const state = get();
    return {
      limit: state.limit,
      orderBy: state.orderBy,
      orderDirection: state.orderDirection,
      search: overrides.search !== undefined ? overrides.search : state.searchTerm,
      cursor: overrides.cursor !== undefined ? overrides.cursor : state.endCursor,
      ...overrides,
    };
  };

  /**
   * Generic fetch handler for employee data
   */
  const fetchEmployees = async ({
    params,
    append = false,
    operation = 'fetch',
    stateUpdates = {},
  }) => {
    try {
      set({ loading: true, error: null, ...stateUpdates });

      const response = await employeesApi.getAllEmployeesLite(params);

      set({
        employees: append
          ? [...get().employees, ...(response.items || [])]
          : response.items || [],
        hasNextPage: response.pageInfo?.hasNextPage || false,
        endCursor: response.pageInfo?.endCursor || null,
        totalCount: response.pageInfo?.totalCount || 0,
      });
    } catch (error) {
      console.error(`Error ${operation} employees`);
      set({ error: error.message || `Failed to ${operation} employees` });
    } finally {
      set({ loading: false });
    }
  };

  return {
    ...initialState,

    /**
     * Initialize employee data (first load or with search/filter)
     */
    initialize: async (searchParams = {}) => {
      if (get().loading) return;
      await fetchEmployees({
        params: buildParams(searchParams),
        operation: 'initialize',
        stateUpdates: { searchTerm: searchParams.search || '' },
      });
    },

    /**
     * Load next page of employees for infinite scrolling
     */
    loadMore: async () => {
      if (get().loading || !get().hasNextPage) return;
      await fetchEmployees({
        params: buildParams({ cursor: get().endCursor }),
        append: true,
        operation: 'load more',
      });
    },

    /**
     * Search for employees by name or email
     */
    searchEmployees: async (searchTerm) => {
      if (get().loading || get().searchTerm === searchTerm) return;
      await fetchEmployees({
        params: buildParams({ search: searchTerm, cursor: null }),
        operation: 'search',
        stateUpdates: { searchTerm },
      });
    },

    /**
     * Reset all store state to initial
     */
    reset: () => set({ ...initialState }),
  };
});

export default useEmployeeStore;